import { STATUS_BAR_DEFAULT_STYLE } from "@/constants/statusBar";
import { StatusBarStyleEnum } from "@/enums/global";
import { isInternetAvailable, handleAppUpdates } from "@/methods/appUtils";
import { blurActiveElement } from "@/methods/focus";
import { allRoutes } from "@/routes/allRoutes";
import { setAppRouter } from "@/routerInstance";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";
import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "@ionic/vue-router";

const missingRoute = [{ path: "/:pathMatch(.*)*", redirect: "/" }];

const routes = allRoutes.concat(missingRoute as Array<RouteRecordRaw>);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes as Array<RouteRecordRaw>,
  scrollBehavior(to, _, savedPosition) {
    const activePage = document.querySelector(
      "ion-router-outlet > .ion-page.ion-page-invisible-not, ion-router-outlet > .ion-page",
    );
    const activeIonContent = activePage?.querySelector(
      "ion-content",
    ) as HTMLIonContentElement | null;
    if (activeIonContent) {
      if (savedPosition && !to.meta.scrollToTop) {
        void activeIonContent.scrollToPoint(savedPosition.left ?? 0, savedPosition.top, 300);
      } else {
        void activeIonContent.scrollToTop(300);
      }
      return false;
    }
    if (savedPosition && !to.meta.scrollToTop) {
      return savedPosition;
    } else {
      return { top: 0, behavior: "smooth" };
    }
  },
});

// Register the instance for store/utility consumers (see src/routerInstance.ts —
// importing "@/router" from those modules creates a circular module graph).
setAppRouter(router);

function updateLoaderText(text: string) {
  const loaderText = document.getElementById("app-loader-text");
  if (loaderText) loaderText.textContent = text;
}

router.beforeEach(async (to, from) => {
  // Drop focus from the control that triggered this navigation before Ionic marks the outgoing
  // page `ion-page-hidden` + `aria-hidden` for the transition — otherwise the still-focused button
  // trips Chrome's "Blocked aria-hidden ... retained focus" warning. See @/methods/focus.
  blurActiveElement();
  const sessionStore = useSessionStore();
  const { LastPingStatusCode } = storeToRefs(sessionStore);
  const hasInternet = await isInternetAvailable();
  const allowOffline = to.meta.allowOffline as boolean;
  if (to.name === "CheckInternetConnection" || (allowOffline && !hasInternet)) {
    await sessionStore.afterNavigateValidated();
    return true;
  }
  if (to.name === "UnderMaintenance" || to.name === "TooManyRequests") {
    if (from.name === undefined) {
      updateLoaderText("Loading your dashboard...");
      await sessionStore.beforeNavigateStart();
      if (LastPingStatusCode.value === HttpStatusCode.Ok) {
        return { name: "LandingPage" };
      }
    }
    await sessionStore.afterNavigateValidated();
    return true;
  }
  updateLoaderText("Connecting to server...");
  if (!hasInternet) {
    return { name: "CheckInternetConnection" };
  }
  await sessionStore.beforeNavigateStart();
  if (LastPingStatusCode.value !== HttpStatusCode.Ok) {
    if (LastPingStatusCode.value === HttpStatusCode.TooManyRequests) {
      return { name: "TooManyRequests" };
    }
    return { name: "UnderMaintenance" };
  }
  const appSettingsStore = useAppSettingsStore();
  if (from.name === undefined && to.name !== "CheckInternetConnection" && to.name !== "Logout") {
    try {
      updateLoaderText("Loading configuration...");
      await appSettingsStore.initializeAppConfig();
      updateLoaderText("Preparing your dashboard...");
      const isValidSession = await sessionStore.validateLoggedInSession();
      if (!isValidSession) {
        sessionStore.hideAppLoader();
        return false;
      }
    } catch {
      sessionStore.hideAppLoader();
      return { name: "UnderMaintenance" };
    }
  }
  let isValidNavigation: boolean;
  const { IsLoggedIn, UserInfo } = storeToRefs(sessionStore);
  if (to.meta.requiresAuth) {
    if (IsLoggedIn.value) {
      const allowedRoleIds = (to.meta.allowedRoleIds as Array<number>) || [];
      const userRoleId = UserInfo.value.RoleId;
      if (allowedRoleIds.includes(userRoleId)) {
        isValidNavigation = true;
      } else {
        return { name: "Unauthorized" };
      }
    } else {
      return { name: "LandingPage" };
    }
  } else if (to.meta.requiresUnAuth && IsLoggedIn.value && appSettingsStore.AppMenu) {
    return { name: appSettingsStore.AppMenu.DefaultView };
  } else {
    isValidNavigation = true;
  }
  if (isValidNavigation) {
    // While the forced password-change gate is active, the session-touch call would 403 (the
    // backend rejects authenticated calls until the password changes) and cascade into a logout —
    // skip it; the gate's only permitted authenticated call is changePassword itself.
    if (IsLoggedIn.value && to.name !== "Logout" && !sessionStore.IsMustChangePasswordActive) {
      await sessionStore.updateSessionLastAccessed();
    }
    await sessionStore.afterNavigateValidated();
    if (to.name !== "Logout") {
      await handleAppUpdates();
    }
    sessionStore.hideAppLoader();
  }
});

router.afterEach((to) => {
  const appSettingsStore = useAppSettingsStore();
  const { HideNavbar, ShowAppFooter, HideNavbarOnScroll } = storeToRefs(appSettingsStore);
  HideNavbar.value = !!to.meta.excludeNavbar;
  ShowAppFooter.value = !!to.meta.showAppFooter;
  HideNavbarOnScroll.value = false;
  const desiredStyle =
    (to.meta.statusBarStyle as StatusBarStyleEnum | undefined) ?? STATUS_BAR_DEFAULT_STYLE;
  if (desiredStyle !== appSettingsStore.StatusBarStyle) {
    appSettingsStore.setStatusBarStyle(desiredStyle);
  }
});

export default router;
